import { Box, Button, Flex, HStack, Tag, Text, VStack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { djs } from '@duke-hq/djs';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { PaginatedCourse } from './types';

export function SupersessionSummary({
  currentCourse,
  onClose,
  title,
}: {
  currentCourse: PaginatedCourse;
  onClose: () => void;
  title: string;
}) {
  const { data: supersessionHistory } = trpcAdmin.courses.courseSupersessionHistory.useQuery({
    courseId: currentCourse.id,
  });

  const { data: currentCourseData } = trpcAdmin.courses.byId.useQuery(currentCourse.id);

  const supersedingCourse = supersessionHistory?.supersededByCourse;
  const effectiveDate = supersessionHistory?.supersededEffectiveDate;
  const teachOutDate = supersessionHistory?.teachOutEndDate;
  const notes = supersessionHistory?.reason;

  const unitMappings =
    currentCourseData?.courseItems
      .filter((item) => item.unitOfCompetency != null)
      .map((item) => ({
        id: item.id,
        unit: item.unitOfCompetency,
        supersededByUnit: item.supersededByUnit,
      })) ?? [];

  return (
    <Box display="flex" flexDirection="column">
      <Text fontSize="lg" fontWeight="semibold" mb={4}>
        {title}
      </Text>

      <VStack spacing={4} align="stretch">
        <Box>
          <Text fontSize="sm" color="fg.subtle" fontWeight="medium" mb={1}>
            <Trans>Superseded by</Trans>
          </Text>
          {supersedingCourse ? (
            <HStack>
              {supersedingCourse.identifier != null && supersedingCourse.identifier !== '' ? (
                <Text fontSize="sm" fontWeight="medium" color="fg.subtle">
                  {supersedingCourse.identifier}
                </Text>
              ) : null}
              <Text fontSize="sm">{supersedingCourse.title}</Text>
            </HStack>
          ) : (
            <Text fontSize="sm" color="fg.muted">
              <Trans>Not set</Trans>
            </Text>
          )}
        </Box>

        <HStack spacing={8} align="start">
          <Box>
            <Text fontSize="sm" color="fg.subtle" fontWeight="medium" mb={1}>
              <Trans>Effective date</Trans>
            </Text>
            <Text fontSize="sm">
              {effectiveDate != null ? djs(effectiveDate).format('DD MMM YYYY') : '—'}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" color="fg.subtle" fontWeight="medium" mb={1}>
              <Trans>Teach-out date</Trans>
            </Text>
            <Text fontSize="sm">
              {teachOutDate != null ? djs(teachOutDate).format('DD MMM YYYY') : '—'}
            </Text>
          </Box>
        </HStack>

        {notes != null && notes !== '' ? (
          <Box>
            <Text fontSize="sm" color="fg.subtle" fontWeight="medium" mb={1}>
              <Trans>Notes</Trans>
            </Text>
            <Text fontSize="sm" whiteSpace="pre-wrap">
              {notes}
            </Text>
          </Box>
        ) : null}

        {unitMappings.length > 0 ? (
          <VStack
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="md"
            align="stretch"
            spacing={0}
          >
            <Text
              fontWeight="medium"
              fontSize="sm"
              padding={3}
              borderBottomWidth="1px"
              borderColor="border.default"
            >
              <Trans>Unit mappings</Trans>
            </Text>
            <Box maxHeight={pxToRem(320)} overflowY="auto">
              {unitMappings.map(({ id, unit, supersededByUnit }) => {
                if (unit == null) {
                  return null;
                }
                return (
                  <VStack
                    key={id}
                    py={3}
                    px={4}
                    borderBottomWidth="1px"
                    borderColor="border.default"
                    align="start"
                    spacing={2}
                    _last={{ borderBottomWidth: 0 }}
                  >
                    <HStack>
                      <Text fontSize="sm" fontWeight="medium">
                        {unit.code}
                      </Text>
                      {Boolean(unit.unitContextCategory) && (
                        <Tag
                          size="sm"
                          fontSize="xs"
                          fontWeight="bold"
                          colorScheme="blackAlpha"
                          variant="solid"
                        >
                          {unit.unitContextCategory?.categoryName}
                        </Tag>
                      )}
                      <Text fontSize="sm" noOfLines={1}>
                        {unit.title}
                      </Text>
                    </HStack>
                    {supersededByUnit ? (
                      <HStack>
                        <Text fontSize="sm" color="fg.subtle" aria-hidden="true">
                          →
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {supersededByUnit.code}
                        </Text>
                        <Text fontSize="sm">{supersededByUnit.title}</Text>
                      </HStack>
                    ) : (
                      <Text fontSize="sm" color="fg.muted">
                        <Trans>No equivalent unit mapping</Trans>
                      </Text>
                    )}
                  </VStack>
                );
              })}
            </Box>
          </VStack>
        ) : null}
      </VStack>

      <Flex justifyContent="flex-end" pt={4}>
        <Button colorScheme="gray" onClick={onClose}>
          <Trans>Close</Trans>
        </Button>
      </Flex>
    </Box>
  );
}
